import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Home } from '../../icons';
import { NAVIGATION_CONFIG, NavSection } from '../../data/staticData';
import { useThemeContext } from '../../context/ThemeContext';
import * as Icons from '../../icons';

interface MegaMenuProps {
}

const getIcon = (iconName: string) => {
    const IconComponent = (Icons as Record<string, React.FC<{ className?: string }>>)[iconName];
    return IconComponent || Icons.FileText;
};

const MegaMenu: React.FC<MegaMenuProps> = () => {
    const { isDarkMode } = useThemeContext();
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const handleMouseEnter = (sectionId: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setActiveSection(sectionId);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setActiveSection(null);
        }, 150);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveSection(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const baseTextClass = isDarkMode ? 'text-gray-300' : 'text-gray-700';
    const hoverTextClass = isDarkMode ? 'hover:text-white' : 'hover:text-gray-900';
    const activeTextClass = isDarkMode ? 'text-white' : 'text-gray-900';
    const dropdownBgClass = isDarkMode
        ? 'bg-slate-900/98 border-white/10'
        : 'bg-white/98 border-gray-200';

    return (
        <nav ref={menuRef} className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation">
            <Link
                to="/"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${baseTextClass} ${hoverTextClass}`}
            >
                <Home className="w-4 h-4" aria-hidden="true" />
                <span>Home</span>
            </Link>

            {NAVIGATION_CONFIG.map((section: NavSection) => {
                const SectionIcon = getIcon(section.icon);
                const isActive = activeSection === section.id;

                // Direct link (no dropdown) if section has path and no items
                if (section.path && section.items.length === 0) {
                    return (
                        <Link
                            key={section.id}
                            to={section.path}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-all ${baseTextClass} ${hoverTextClass}`}
                        >
                            <SectionIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{section.label}</span>
                        </Link>
                    );
                }

                return (
                    <div
                        key={section.id}
                        className="relative"
                        onMouseEnter={() => handleMouseEnter(section.id)}
                        onMouseLeave={handleMouseLeave}
                    >
                        <button
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-all ${isActive ? activeTextClass : baseTextClass
                                } ${hoverTextClass} ${isActive ? (isDarkMode ? 'bg-white/10' : 'bg-gray-100') : ''}`}
                            aria-expanded={isActive}
                            aria-haspopup="true"
                        >
                            <SectionIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{section.label}</span>
                            <ChevronDown
                                className={`w-3.5 h-3.5 transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`}
                                aria-hidden="true"
                            />
                        </button>

                        <div
                            className={`absolute top-full left-0 pt-2 transition-all duration-200 ${isActive ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                                }`}
                        >
                            <div
                                className={`min-w-[320px] rounded-xl shadow-2xl border backdrop-blur-md p-4 ${dropdownBgClass}`}
                            >
                                <div className="grid gap-1">
                                    {section.items.map((item) => {
                                        const ItemIcon = getIcon(item.icon);
                                        return (
                                            <Link
                                                key={item.id}
                                                to={item.path}
                                                onClick={() => setActiveSection(null)}
                                                className={`flex items-start gap-3 p-3 rounded-lg transition-all group ${isDarkMode
                                                        ? 'hover:bg-white/10'
                                                        : 'hover:bg-gray-100'
                                                    }`}
                                            >
                                                <div className={`p-2 rounded-lg ${isDarkMode
                                                        ? 'bg-white/10 text-blue-400'
                                                        : 'bg-blue-50 text-blue-600'
                                                    }`}>
                                                    <ItemIcon className="w-4 h-4" aria-hidden="true" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                        {item.label}
                                                    </div>
                                                    {item.description && (
                                                        <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                                            }`}>
                                                            {item.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </nav>
    );
};

export default MegaMenu;
